import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@fluentui/react-components";
import SidebarSection from "../../app-sidebar/SidebarSection";
import SidebarShowMore from "../../app-sidebar/SidebarShowMore";
import { AddRegular } from "@fluentui/react-icons/svg/add";
import { EditRegular } from "@fluentui/react-icons/svg/edit";
import { ErrorCircleFilled } from "@fluentui/react-icons/svg/error-circle";
import { useSidebarConnectionsViewModel } from "./SidebarConnections.ViewModel";
import type { UseSidebarConnectionsViewModelArgs } from "./SidebarConnections.ViewModel.types";
import "./SidebarConnections.scss";

// Conditionally wraps a ReactElement in a Fluent UI Tooltip (for collapsed icon-only mode)
const MaybeTooltip: React.FC<{
  show: boolean;
  content: string;
  children: React.ReactElement;
}> = ({ show, content, children }) =>
  show ? (
    <Tooltip content={content} relationship="label" positioning="after">
      {children}
    </Tooltip>
  ) : (
    children
  );

const SidebarConnections: React.FC<UseSidebarConnectionsViewModelArgs> = ({
  isCollapsed,
  isMobileOpen,
  onMobileClose,
}) => {
  const { t } = useTranslation();
  const vm = useSidebarConnectionsViewModel({
    isCollapsed,
    isMobileOpen,
    onMobileClose,
  });

  return (
    <SidebarSection
      title={t("layout.tabs.connections")}
      isOpen={vm.isSectionOpen}
      onToggle={vm.toggleSection}
      isPaneCollapsed={isCollapsed}
    >
      {vm.visibleConnections.map((connection) => {
        // A failed connection says so, and says why where the server recorded a
        // reason. Collapsed, the tooltip is the only room there is for it.
        const failureLabel = connection.failureReason
          ? t("layout.sidebar.connectionSyncFailedWithReason", {
              name: connection.displayName,
              reason: connection.failureReason,
            })
          : connection.hasFailed
            ? t("layout.sidebar.connectionNotAuthorised", {
                name: connection.displayName,
              })
            : null;

        const tooltipContent =
          failureLabel ??
          (connection.count !== undefined
            ? `${connection.displayName} (${connection.count})`
            : connection.displayName);

        const linkClass = [
          "sidebar-nav__link",
          "sidebar-nav__link--compact",
          "sidebar-nav__link--source",
          connection.isSelected ? "sidebar-nav__link--selected" : "",
          connection.hasFailed ? "sidebar-nav__link--unauthorised" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <MaybeTooltip
            key={connection.id}
            show={isCollapsed}
            content={tooltipContent}
          >
            <div className="sidebar-connections__item">
              <button
                className={linkClass}
                type="button"
                onClick={(e) =>
                  vm.onConnectionClick(connection.id, e.ctrlKey || e.metaKey)
                }
                onDoubleClick={() => vm.onEditClick(connection.id)}
                aria-label={
                  failureLabel ??
                  (connection.isSelected
                    ? t("layout.sidebar.connectionFilterActive", {
                        name: connection.displayName,
                      })
                    : connection.displayName)
                }
              >
                {connection.colour && (
                  <span
                    className="sidebar-nav__colour-bar"
                    style={{ background: connection.colour }}
                  />
                )}
                <img
                  className="sidebar-nav__source-icon"
                  src={`${vm.iconBaseUrl}/assets/img/sourceicons/${connection.sourceTypeId.toLowerCase()}.png`}
                  alt=""
                  aria-hidden="true"
                />
                {!isCollapsed && (
                  <span className="sidebar-nav__label">
                    {connection.displayName}
                  </span>
                )}

                {/* Collapsed keeps the number: a count is the one thing the
                    rail can still say in 76px, and without it a collapsed pane
                    is a column of logos that reports nothing. */}
                {connection.hasFailed ? (
                  <span
                    className="sidebar-connections__failure-icon"
                    title={failureLabel ?? undefined}
                  >
                    <ErrorCircleFilled aria-hidden="true" />
                  </span>
                ) : (
                  // Every connection carries one, zero included: collapsed, the
                  // slot is what keeps the source icons in a single column.
                  <span
                    className={`sidebar-nav__badge${connection.count === 0 ? " sidebar-nav__badge--zero" : ""}`}
                  >
                    {connection.count}
                  </span>
                )}

                {!isCollapsed && (
                  <>
                    <button
                      className="sidebar-connections__edit-btn"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        vm.onEditClick(connection.id);
                      }}
                      aria-label={t("layout.sidebar.editConnection", {
                        name: connection.displayName,
                      })}
                    >
                      <EditRegular />
                    </button>
                  </>
                )}
              </button>
            </div>
          </MaybeTooltip>
        );
      })}

      <SidebarShowMore
        hiddenCount={vm.hiddenCount}
        isExpanded={vm.isExpanded}
        isCollapsed={isCollapsed}
        onToggle={vm.toggleExpanded}
      />

      {/* Not on the rail: it is setup rather than a filter, and a bare "+"
          among the filters read as one. Expand the pane to add a connection. */}
      {!isCollapsed && (
        <button
          type="button"
          className="sidebar-nav__link sidebar-nav__link--compact"
          data-tour="sidebar-add-connection"
          onClick={vm.onAddConnectionClick}
        >
          <AddRegular className="sidebar-nav__icon" />
          <span className="sidebar-nav__label">
            {t("layout.sidebar.addConnection")}
          </span>
        </button>
      )}
    </SidebarSection>
  );
};

export default SidebarConnections;
